<template>
  <div class="feedback-wrapper">
    <TextBody v-if="showThankYouMessage" tag="div" text="Thank you for your feedback!" :body-type="1" weight="medium" />
    <div v-if="!showThankYouMessage" class="feedback-container">
      <div class="like-dislike-container">
        <BbBtn
          size="small"
          type="secondary"
          icon="thumbsup"
          icon-type="outlined"
          is-icon-button
          without-background
          @click="likeMessage"
        />
        <BbBtn
          size="small"
          type="secondary"
          icon="thumbsdown"
          icon-type="outlined"
          is-icon-button
          without-background
          @click="dislikeMessage"
        />
      </div>
      <v-form ref="form" v-model="formState.valid">
        <div class="reason-container" v-if="isReasonShown">
          <TextBody tag="span" i18n-key="feedback.couldYouTellUsWhy" :body-type="1" weight="medium" />
          <BbTextarea
            variant="outlined"
            v-model="formState.reason"
            :placeholder="reasonPlaceholder"
            :rows="1"
            :rules="rules"
            :auto-grow="true"
            @keydown.enter.prevent="submitFeedback"
          />
        </div>
        <div class="like-dislike-container" v-if="isReasonShown">
          <BbBtn size="small" type="secondary" @click="submitFeedback">
            <TextBody i18n-key="feedback.submitFeedback" />
          </BbBtn>
          <BbBtn size="small" type="secondary" @click="cancelReason">
            <TextBody i18n-key="common.cancel" />
          </BbBtn>
        </div>
      </v-form>
    </div>
  </div>
</template>

<script>
import { computed, inject, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n/dist/vue-i18n';
import { v4 as uuidv4 } from 'uuid';

import { GetCurrentConversation } from '~/constants/getters';
import { queryType } from '~/constants/conversationModels';

import TextBody from '~/components/common/TextBody';

export default {
  components: { TextBody },
  props: {
    messageId: {
      type: String,
      default: '',
    },
  },
  setup(props) {
    const store = useStore();
    const { t } = useI18n();

    const realtime = inject('realtime');

    const currentConversation = computed(() => store.getters[`conversationStore/${GetCurrentConversation}`]);
    const conversationId = computed(() => currentConversation.value.conversationId);

    const form = ref(null); // Reference to the form
    const formState = reactive({
      valid: true, // Overall form validity
      reason: '', // Model for the v-textarea
    });
    const rules = [(v) => !!v || t('feedback.reason')];
    const reason = ref('');
    const isReasonShown = ref(false);
    const showThankYouMessage = ref(false);
    const reasonPlaceholder = t('feedback.whatsNotQuiteRight');

    const publishSentiment = (sentiment, reason = '') => {
      //need this conversion to seconds for python compatibility timestamps
      const timestamp = Math.floor(Date.now() / 1000);
      const correlationId = uuidv4();

      // at the moment we have no state for liked/unliked
      // store.dispatch(`conversationStore/${UpdateMessageSentiment}`, { messageId: props.messageId, sentiment });

      realtime.publishToUserChannel({
        channel: `conversations-query:${conversationId.value}`,
        topic: '',
        message: {
          data: {
            // has no business use, only to respect python ably event object
            correlationId,
            responseMessageId: props.messageId,
            message_type: queryType.sentiment,
            timestamp, // need to convert string to base64 to avoid LLM throwing errors (when reasons contains special characters that need to be escaped)
            user_feedback: { liked: sentiment, message_id: props.messageId, reason: btoa(reason) },
          },
        },
      });
    };

    const likeMessage = () => {
      // Show the thank you message
      showThankYouMessage.value = true;
      isReasonShown.value = false;
      publishSentiment(true);

      // Hide the thank you message after 5 seconds
      setTimeout(() => {
        showThankYouMessage.value = false;
      }, 5000);
    };

    const dislikeMessage = () => {
      isReasonShown.value = true;
    };

    const cancelReason = () => {
      isReasonShown.value = false;
      formState.reason = '';
    };

    const submitFeedback = async () => {
      const formValidation = await form.value.validate();
      if (formValidation.valid) {
        // Proceed with form submission
        publishSentiment(false, formState.reason);

        // Show the thank you message
        showThankYouMessage.value = true;
        isReasonShown.value = false;
        formState.reason = '';

        // Hide the thank you message after 5 seconds
        setTimeout(() => {
          showThankYouMessage.value = false;
        }, 5000);
      }
    };

    return {
      cancelReason,
      dislikeMessage,
      form,
      formState,
      isReasonShown,
      likeMessage,
      reason,
      reasonPlaceholder,
      rules,
      showThankYouMessage,
      submitFeedback,
      t,
    };
  },
};
</script>

<style scoped lang="scss">
* {
  box-sizing: border-box;
}

:deep(.body-1) {
  letter-spacing: 0.1px;
}

.feedback-wrapper {
  display: flex;
  align-items: center;
  width: 100%;
}

.feedback-container {
  display: flex;
  flex-direction: column;
  gap: $bb-spacing-2;
  width: 100%;

  .bb-btn {
    .bb-icon {
      margin-right: $bb-spacing-1;
    }
  }

  .like-dislike-container {
    display: flex;
    flex-direction: row;
    align-items: center;

    :deep(.body-1) {
      letter-spacing: 0.1px;
      padding-right: $bb-spacing-4;
    }

    button {
      margin-right: $bb-spacing-1;
    }
  }

  .reason-container {
    display: flex;
    flex-direction: column;
    gap: $bb-spacing-1;

    :deep(.body-1) {
      letter-spacing: 0.1px;
    }

    :deep(.v-input .v-input__control .v-field) {
      border: none;
      background-color: $bb-neutral-0;
      margin-bottom: $bb-spacing-2;

      .v-field__field {
        textarea::placeholder {
          @include bb-regular-text-styles('lg');
          color: $bb-neutral-2;
          align-content: center;
          align-items: center;
        }
      }
    }

    :deep(.v-input .v-input__control .v-field) :not(.v-field__error) {
      .v-field__outline {
        .v-field__outline__start {
          border: 1px solid #ccc;
          border-right: none;
        }

        .v-field__outline__end {
          border: 1px solid #ccc;
          border-left: none;
        }
      }
    }

    :deep(.v-input .v-input__details) {
      padding: 0;

      .v-messages .v-messages__message {
        padding: $bb-spacing-2 0 $bb-spacing-2 0;
      }
    }

    button {
      margin-right: $bb-spacing-1;
    }
  }
}
</style>
